import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Check } from "lucide-react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { useSeo } from "@/hooks/useSeo";

export default function Areas() {
  useSeo(
    "Areas We Cover | Exterior Cleaning Across Lancashire | StoneCrest",
    "StoneCrest provides professional exterior cleaning across Lancashire including Preston, Chorley, Leyland, Lancaster, Blackburn and Blackpool.",
    "https://stonecrestexteriors.co.uk/areas"
  );

  const [areas, setAreas] = useState([]);

  useEffect(() => {
    api
      .get("/areas")
      .then((r) => setAreas(r.data))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="areas-page">
      <PageHeader
        overline="Areas We Cover"
        lines={["Professional exterior cleaning", "across Lancashire."]}
        subtitle="StoneCrest provides professional exterior cleaning for homes, landlords and commercial properties across Lancashire. Select your town below to learn more about the services available in your area."
      />

      {/* Intro */}
      <section className="px-6 md:px-12 max-w-7xl mx-auto pb-12">
        <div className="max-w-3xl space-y-5 text-lg text-[var(--muted)] leading-relaxed">
          <Reveal>
            <p>
              We provide exterior cleaning throughout Lancashire, helping
              homeowners and businesses restore roofs, render, driveways,
              patios, gutters, fascias and soffits using professional cleaning
              methods selected for each surface.
            </p>
          </Reveal>

          <Reveal delay={0.05}>
            <p>
              Our main service areas include Preston, Chorley, Leyland,
              Blackburn, Blackpool and Lancaster, along with many surrounding
              towns and villages. Every job begins with a property assessment
              and a clear, no-obligation quotation.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Lancashire Overview */}
      <section className="px-6 md:px-12 max-w-7xl mx-auto pb-12">
        <Reveal>
          <Link
            to="/areas/lancashire"
            data-testid="areas-lancashire-link"
            className="group flex flex-col md:flex-row md:items-center md:justify-between gap-6 border border-[var(--border)] p-7 md:p-9 hover:border-[var(--oxblood)] transition-colors"
          >
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[var(--oxblood)] mb-3">
                Lancashire Overview
              </p>

              <h2 className="font-serif font-light text-2xl md:text-3xl mb-3">
                Exterior cleaning across Lancashire
              </h2>

              <p className="text-[var(--muted)] leading-relaxed max-w-2xl">
                Explore our county-wide exterior cleaning services, including
                roof cleaning, render softwashing, driveway and patio cleaning,
                gutter clearing and fascia and soffit cleaning.
              </p>
            </div>

            <div className="inline-flex items-center gap-2 text-[var(--oxblood)] shrink-0">
              View Lancashire services
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </div>
          </Link>
        </Reveal>
      </section>

      {/* Town Grid */}
      <section className="px-6 md:px-12 max-w-7xl mx-auto pb-20 md:pb-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-[var(--oxblood)] mb-4">
            Local Service Areas
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="font-serif font-light text-3xl md:text-5xl leading-tight mb-10 max-w-3xl">
            Find exterior cleaning services near you.
          </h2>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border)] border border-[var(--border)]">
          {areas.map((a, i) => (
            <Reveal key={a.slug} delay={(i % 3) * 0.06}>
              <Link
                to={`/areas/${a.slug}`}
                data-testid={`area-card-${a.slug}`}
                className="group bg-white p-10 block hover:bg-[var(--oxblood)] hover:text-white transition-colors duration-500 h-full"
              >
                <MapPin
                  className="w-6 h-6 text-[var(--oxblood)] group-hover:text-white mb-6 transition-colors"
                  strokeWidth={1.3}
                />

                <h3 className="font-serif text-3xl mb-3 flex items-center justify-between">
                  {a.name}

                  <ArrowUpRight className="w-5 h-5 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
                </h3>

                <p className="text-sm text-[var(--muted)] group-hover:text-white/80 leading-relaxed transition-colors line-clamp-3">
                  {a.intro}
                </p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Services */}
      <section className="bg-[var(--bone)]">
        <div className="px-6 md:px-12 max-w-7xl mx-auto py-16 md:py-20">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[var(--oxblood)] mb-4">
              Services Available
            </p>
          </Reveal>

          <Reveal delay={0.05}>
            <h2 className="font-serif font-light text-3xl md:text-5xl leading-tight mb-10 max-w-3xl">
              Exterior cleaning services throughout Lancashire.
            </h2>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-3">
            <Link
              to="/services/roof-cleaning"
              className="group flex items-center gap-3 border-t border-[var(--border)] py-4 hover:text-[var(--oxblood)] transition-colors"
            >
              <Check className="w-5 h-5 text-[var(--oxblood)] shrink-0" />
              <span className="group-hover:translate-x-1 transition-transform">
                Roof Cleaning
              </span>
            </Link>

            <Link
              to="/services/render-softwashing"
              className="group flex items-center gap-3 border-t border-[var(--border)] py-4 hover:text-[var(--oxblood)] transition-colors"
            >
              <Check className="w-5 h-5 text-[var(--oxblood)] shrink-0" />
              <span className="group-hover:translate-x-1 transition-transform">
                Render Softwashing
              </span>
            </Link>

            <Link
              to="/services/driveway-patio-cleaning"
              className="group flex items-center gap-3 border-t border-[var(--border)] py-4 hover:text-[var(--oxblood)] transition-colors"
            >
              <Check className="w-5 h-5 text-[var(--oxblood)] shrink-0" />
              <span className="group-hover:translate-x-1 transition-transform">
                Driveway & Patio Cleaning
              </span>
            </Link>

            <Link
              to="/services/gutter-clearing"
              className="group flex items-center gap-3 border-t border-[var(--border)] py-4 hover:text-[var(--oxblood)] transition-colors"
            >
              <Check className="w-5 h-5 text-[var(--oxblood)] shrink-0" />
              <span className="group-hover:translate-x-1 transition-transform">
                Gutter Clearing
              </span>
            </Link>

            <Link
              to="/services/fascia-soffit-cleaning"
              className="group flex items-center gap-3 border-t border-[var(--border)] py-4 hover:text-[var(--oxblood)] transition-colors"
            >
              <Check className="w-5 h-5 text-[var(--oxblood)] shrink-0" />
              <span className="group-hover:translate-x-1 transition-transform">
                Fascia & Soffit Cleaning
              </span>
            </Link>

            <Link
              to="/quote"
              className="group flex items-center gap-3 border-t border-[var(--border)] py-4 hover:text-[var(--oxblood)] transition-colors"
            >
              <Check className="w-5 h-5 text-[var(--oxblood)] shrink-0" />
              <span className="group-hover:translate-x-1 transition-transform">
                Free Fixed-Price Quotes
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Coverage Note */}
      <section className="px-6 md:px-12 max-w-4xl mx-auto py-16 md:py-20 text-center">
        <Reveal>
          <h2 className="font-serif font-light text-3xl md:text-4xl mb-5">
            Don't see your town listed?
          </h2>
        </Reveal>

        <Reveal delay={0.05}>
          <p className="text-lg text-[var(--muted)] leading-relaxed mb-7">
            We cover many additional towns and villages throughout Lancashire.
            Contact us with your postcode and we will confirm whether we can
            provide exterior cleaning in your area.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 text-[var(--oxblood)] border-b border-[var(--oxblood)] pb-1"
          >
            Check your area
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </section>

      <CTASection />
    </div>
  );
}